import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { API_BASE_URL as API_URL } from '../../../config/api.js';
import type { StyleGuideline } from '../../../types/article-brief.js';

// Query key factory for post-related queries
export const postQueryKeys = {
    all: ['post'] as const,
    styleGuides: () => [...postQueryKeys.all, 'styleGuides'] as const,
};

// Types with proper constraints matching backend Zod schema
export type Platform = 'linkedin' | 'twitter' | 'email';
export type Voice = 'contrarian' | 'educational' | 'vulnerable' | 'executive';
export type Goal = 'traffic' | 'debate' | 'summary' | 'realization';
export type PostLength = 'tweet' | 'standard' | 'storytelling';

interface GeneratePostParams {
    articleId: string;
    platform?: Platform;
    voices?: Voice[];
    goals?: Goal[];
    length?: PostLength;
    styleGuideId: string;
    customPrompt?: string;
    model?: string;
}

interface GeneratePostResult {
    success: boolean;
    post: string;
    platform: string;
    articleId: string;
}

export function useGeneratePost() {
    const { getToken } = useAuth();

    return useMutation<GeneratePostResult, Error, GeneratePostParams>({
        mutationFn: async ({ articleId, platform, voices, goals, length, styleGuideId, customPrompt, model }) => {
            const token = await getToken();
            const headers: HeadersInit = {
                'Content-Type': 'application/json',
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_URL}/api/post/generate`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ articleId, platform, voices, goals, length, styleGuideId, customPrompt, model }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to generate post');
            }

            return response.json();
        },
    });
}

// Style Guide Types
interface StyleGuidesResponse {
    success: boolean;
    styleGuides: StyleGuideline[];
}

// Hook to fetch style guides
export function useStyleGuides() {
    const { getToken } = useAuth();

    return useQuery<StyleGuidesResponse, Error>({
        queryKey: postQueryKeys.styleGuides(),
        queryFn: async () => {
            const token = await getToken();
            const headers: HeadersInit = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_URL}/api/style-guides`, { headers });
            if (!response.ok) {
                throw new Error('Failed to fetch style guides');
            }
            return response.json();
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}
